// Phase MHM-1 · Hub Agents — list + tabs + approve/reject/penalty + live map.
// MHM-5.1 — Consumes `?filter=active|pending|suspended` from URL.
import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { Loader2, RefreshCw, Check, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";
import MHMSubNav from "../../components/hub/MHMSubNav";
import RoleGate from "../../components/RoleGate";

// Fix default Leaflet icon
const defaultIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

const TABS = ["active", "pending", "suspended"];

function LiveMap({ rows }) {
  const onlineRows = rows.filter((r) => r.lat != null && r.lng != null);
  const center = onlineRows.length
    ? [onlineRows[0].lat, onlineRows[0].lng]
    : [12.9716, 77.5946]; // default Bangalore

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-3" data-testid="mhm-agents-map-card">
      <h3 className="text-sm font-semibold text-slate-700 mb-2 font-['Outfit']">
        Live agent positions · {onlineRows.length} live
      </h3>
      <div className="h-[260px] rounded-lg overflow-hidden border border-slate-100" data-testid="mhm-agents-map">
        <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%" }}
                       scrollWheelZoom={false}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; OpenStreetMap'
          />
          {onlineRows.map((r) => (
            <Marker key={r.agent_user_id} position={[r.lat, r.lng]} icon={defaultIcon}>
              <Popup>
                <div className="text-xs">
                  <div className="font-semibold">{r.name}</div>
                  <div>{r.hub_id}</div>
                  <div className={r.stale ? "text-amber-600" : "text-green-600"}>
                    {r.stale ? "Stale (>60s)" : "Fresh"}
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
}

export default function HubAgents() {
  const [searchParams] = useSearchParams();
  const initialTab = (() => {
    const f = searchParams.get("filter");
    return TABS.includes(f) ? f : "active";
  })();
  const [tab, setTab] = useState(initialTab);
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [penaltyOpen, setPenaltyOpen] = useState(null);
  const [pAmount, setPAmount] = useState(50);
  const [pReason, setPReason] = useState("");
  const locTimer = useRef(null);

  // Reflect URL changes (e.g. user clicks another KPI without refresh)
  useEffect(() => {
    const f = searchParams.get("filter");
    if (f && TABS.includes(f) && f !== tab) setTab(f);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/hub/agents?status=${tab}`);
      setRows(data.rows || []);
    } catch (e) { toast.error(e.userMessage || "Couldn't load agents"); }
    finally { setLoading(false); }
  }, [tab]);

  const loadLocations = useCallback(async () => {
    try {
      const { data } = await api.get("/hub/agents/locations");
      setLocations(data.rows || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    loadLocations();
    locTimer.current = setInterval(loadLocations, 15000);
    return () => clearInterval(locTimer.current);
  }, [loadLocations]);

  const approve = async (a) => {
    try {
      await api.post(`/hub/agents/${a.id}/approve`, {});
      toast.success(`${a.name} approved`); load();
    } catch (e) { toast.error(e.userMessage || "Approve failed"); }
  };
  const reject = async (a) => {
    const reason = window.prompt(`Reject ${a.name}? Reason:`);
    if (!reason) return;
    try {
      await api.post(`/hub/agents/${a.id}/reject`, { reason });
      toast.success(`${a.name} rejected`); load();
    } catch (e) { toast.error(e.userMessage || "Reject failed"); }
  };
  const toggleAvail = async (a) => {
    try {
      await api.patch(`/hub/agents/${a.id}/availability`, {
        available: a.status === "suspended",
      });
      toast.success("Availability updated"); load();
    } catch (e) { toast.error(e.userMessage || "Failed"); }
  };
  const applyPenalty = async () => {
    if (!penaltyOpen) return;
    if (!pAmount || pAmount <= 0) return toast.error("Amount must be > 0");
    if (!pReason) return toast.error("Reason required");
    try {
      await api.post(`/hub/agents/${penaltyOpen.id}/penalty`, {
        amount: Number(pAmount), reason: pReason,
      });
      toast.success("Penalty applied");
      setPenaltyOpen(null); setPAmount(50); setPReason("");
      load();
    } catch (e) { toast.error(e.userMessage || "Penalty failed"); }
  };

  return (
    <RoleGate allow={["main_hub_manager", "admin", "founder", "ceo"]}>
      <MHMSubNav />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="mhm-agents-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Outfit']" data-testid="mhm-agents-title">
              Agent Management
            </h1>
            <p className="text-sm text-slate-500">Agents scoped to your main hub · {rows.length}</p>
          </div>
          <button onClick={load}
                  data-testid="mhm-agents-refresh"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 hover:bg-slate-50 rounded-lg">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
          </button>
        </div>

        <LiveMap rows={locations} />

        <div className="flex gap-2" data-testid="mhm-agents-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`mhm-agents-tab-${t}`}
              className={`px-3 py-1.5 rounded-full text-sm capitalize ${
                tab === t ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >{t}</button>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="mhm-agents-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Phone</th>
                <th className="px-3 py-2 text-left">Vehicle</th>
                <th className="px-3 py-2 text-left">Rating</th>
                <th className="px-3 py-2 text-left">Online</th>
                <th className="px-3 py-2 text-left">Wallet</th>
                <th className="px-3 py-2 text-left">Penalties</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && !loading && (
                <tr><td colSpan={10} className="text-center py-10 text-slate-400">No agents</td></tr>
              )}
              {rows.map((a) => (
                <tr key={a.id} data-testid={`mhm-agents-row-${a.id}`} className="hover:bg-slate-50">
                  <td className="px-3 py-2">{a.name || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{a.email}</td>
                  <td className="px-3 py-2 text-xs">{a.phone_masked || "—"}</td>
                  <td className="px-3 py-2 text-xs">{a.vehicle_type || "—"}</td>
                  <td className="px-3 py-2 text-xs">{a.rating != null ? `${a.rating}★` : "—"}</td>
                  <td className="px-3 py-2 text-xs">
                    <span className={a.is_online ? "text-green-600 font-semibold" : "text-slate-400"}>
                      {a.is_online ? "online" : "offline"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs">₹{a.wallet_balance || 0}</td>
                  <td className="px-3 py-2 text-xs">{a.penalties_count}</td>
                  <td className="px-3 py-2">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100">{a.status}</span>
                  </td>
                  <td className="px-3 py-2 text-right text-xs space-x-1">
                    {tab === "pending" && (
                      <>
                        <button onClick={() => approve(a)} data-testid={`mhm-agents-approve-${a.id}`}
                                className="inline-flex items-center px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded">
                          <Check className="h-3 w-3" />
                        </button>
                        <button onClick={() => reject(a)} data-testid={`mhm-agents-reject-${a.id}`}
                                className="inline-flex items-center px-2 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded">
                          <X className="h-3 w-3" />
                        </button>
                      </>
                    )}
                    <button onClick={() => setPenaltyOpen(a)}
                            data-testid={`mhm-agents-penalty-${a.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 border border-amber-200 text-amber-700 hover:bg-amber-50 rounded">
                      <AlertTriangle className="h-3 w-3" /> Penalty
                    </button>
                    <button onClick={() => toggleAvail(a)}
                            data-testid={`mhm-agents-availability-${a.id}`}
                            className="inline-flex items-center px-2 py-1 border border-slate-200 hover:bg-slate-50 rounded">
                      {a.status === "suspended" ? "Reinstate" : "Suspend"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Penalty modal */}
      {penaltyOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="mhm-agents-penalty-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-['Outfit'] text-lg font-semibold">Add penalty · {penaltyOpen.name}</h3>
              <button onClick={() => setPenaltyOpen(null)}><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Amount (₹)</label>
                <input type="number" value={pAmount} onChange={(e) => setPAmount(e.target.value)}
                       data-testid="mhm-agents-penalty-amount"
                       className="w-full border border-slate-200 rounded-lg px-2 py-1.5"/>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Reason</label>
                <input value={pReason} onChange={(e) => setPReason(e.target.value)}
                       data-testid="mhm-agents-penalty-reason"
                       className="w-full border border-slate-200 rounded-lg px-2 py-1.5"/>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setPenaltyOpen(null)} className="text-sm px-3 py-1.5 border border-slate-200 rounded-lg">Cancel</button>
              <button onClick={applyPenalty}
                      data-testid="mhm-agents-penalty-submit"
                      className="text-sm px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg">
                Apply penalty
              </button>
            </div>
          </div>
        </div>
      )}
    </RoleGate>
  );
}
